import { useEffect, useCallback } from "react";
import { MainBusEvents, mainBus } from "utils/bus";
import { Handler } from "mitt";
import { useSelector, useDispatch } from "react-redux";
import { IAppState } from "store/app-state";
import { metrics } from "utils/metrics";
import { toggleFullScreen } from "store/application/application-action";
import { useLocation } from "react-router-dom";
import fscreen from "fscreen";

export const useFullScreen = () => {
  const { isFullScreen } = useSelector((state: IAppState) => state.application);
  const dispatch = useDispatch();
  const location = useLocation();

  const handleFullscreenChange = useCallback(() => {
    const isInFullScreen = fscreen.fullscreenElement !== null;
    dispatch(toggleFullScreen(isInFullScreen));
    if (isInFullScreen) {
      console.debug("Entered fullscreen mode");
    }
  }, [dispatch]);

  const handleFullscreenError = useCallback((e?: Event) => {
    console.debug("Fullscreen Error", e);
  }, []);

  useEffect(() => {
    if (fscreen.fullscreenEnabled) {
      fscreen.addEventListener("fullscreenchange",handleFullscreenChange, false);
      fscreen.addEventListener("fullscreenerror", handleFullscreenError, false);

      return () => {
        fscreen.removeEventListener("fullscreenchange", handleFullscreenChange);
        fscreen.removeEventListener("fullscreenerror", handleFullscreenError);
      };
    }
  });

  useEffect(() => {
    if (isFullScreen) {
      metrics.sendEnterFullscreen();
    }
  }, [isFullScreen]);

  useEffect(() => {
    return () => {
      if (!fscreen.fullscreenEnabled) {
        dispatch(toggleFullScreen(false));
      }

      if (document.fullscreenElement && document.hasFocus()) {
        try {
          fscreen.exitFullscreen();
        } catch (e) {
          console.error(`Could not exit fullscreen: ${e}`);
        }
      }
    };
  }, [location, dispatch]);

  useEffect(() => {
    const enterFullScreen = () => {
      try {
        fscreen.requestFullscreen(document.body);
      } catch (e) {
        console.error(`Could not enter fullscreen: ${e}`);
      }
    };

    const exitFullScreen = () => {
      if (document.hasFocus() && document.fullscreenElement) {
        try {
          fscreen.exitFullscreen();
        } catch (e) {
          console.error(`Could not exit fullscreen: ${e}`);
        }
      }
    };

    const toggleFullscreen = () => {
      if (fscreen.fullscreenEnabled) {
        isFullScreen ? exitFullScreen() : enterFullScreen();
      } else {
        dispatch(toggleFullScreen(!isFullScreen));
      }
    };

    const handlers = {
      "toggle_fullscreen": toggleFullscreen,
    };

    for (const [event, handler] of Object.entries(handlers)) {
      mainBus.on(event as keyof MainBusEvents, handler as Handler);
    }

    return () => {
      for (const event of Object.keys(handlers)) {
        mainBus.off(event as keyof MainBusEvents);
      }
    };
  }, [isFullScreen, dispatch]);
};
